// Batched, fire-and-forget player event log (docs/03 POST /v1/player/log,
// docs/04 self-healing). Errors and notable events are buffered in memory and
// flushed periodically; when offline the buffer simply grows (bounded) and
// drains on the next successful flush. Never throws into the caller — logging
// must not be able to take down playback.

import type { Config } from './config.ts';
import { apiUrl } from './config.ts';
import type { PlayerLogEvent } from './types.ts';

const FLUSH_INTERVAL_MS = 30_000;
const MAX_BUFFER = 200; // drop oldest beyond this so a long outage can't OOM

export class LogBuffer {
  private buf: PlayerLogEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private cfg: Config,
    private getToken: () => string | null,
  ) {}

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  add(level: PlayerLogEvent['level'], code: string, message?: string, itemId?: string): void {
    const ev: PlayerLogEvent = { t: Date.now(), level, code };
    if (message !== undefined) ev.message = message;
    if (itemId !== undefined) ev.item_id = itemId;
    this.buf.push(ev);
    if (this.buf.length > MAX_BUFFER) this.buf.splice(0, this.buf.length - MAX_BUFFER);
    // Mirror to the console for on-device debugging.
    const line = `[player] ${code}${message ? ': ' + message : ''}`;
    if (level === 'error') console.error(line);
    else if (level === 'warn') console.warn(line);
    else console.info(line);
  }

  /** Send the current buffer. On failure the events are re-queued for next time. */
  async flush(): Promise<void> {
    if (this.buf.length === 0) return;
    const token = this.getToken();
    if (!token) return; // nothing to attribute the events to yet
    const batch = this.buf;
    this.buf = [];
    try {
      const res = await fetch(apiUrl(this.cfg, '/v1/player/log'), {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
      if (!res.ok) throw new Error(`log ${res.status}`);
    } catch {
      // Re-queue (bounded) so a transient outage doesn't lose events.
      this.buf = batch.concat(this.buf).slice(-MAX_BUFFER);
    }
  }
}
